import React from 'react'
import { Card, Row, Col, Spinner } from 'react-bootstrap'
import { TokenMetrics } from '../types'
import * as S from '../styles'

interface TokenMetricsCardProps {
  data?: TokenMetrics
  loading: boolean
}

// total = 0 (empresa sem dados) dava NaN% / R$ NaN na tela
const percentOf = (part: number, total: number) =>
  total > 0 ? (part / total) * 100 : 0

export const TokenMetricsCard: React.FC<TokenMetricsCardProps> = ({
  data,
  loading
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  if (loading || !data) {
    return (
      <Card className="h-100">
        <Card.Header>
          <Card.Title as="h5">
            <i className="feather icon-layers text-info mr-2" />
            Métricas de Tokens
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <S.LoadingSpinner>
            <Spinner animation="border" />
          </S.LoadingSpinner>
        </Card.Body>
      </Card>
    )
  }

  const deploymentRate = percentOf(data.tokensDeployed, data.totalTokensCreated)

  return (
    <Card className="h-100">
      <Card.Header>
        <Card.Title as="h5">
          <i className="feather icon-layers text-info mr-2" />
          Tokens de Recebíveis
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Row className="mb-4">
          <Col sm={6}>
            <div className="text-center mb-3">
              <h2 className="text-primary mb-1">
                {formatNumber(data.totalTokensCreated)}
              </h2>
              <p className="text-muted mb-0">Recebíveis Criados</p>
            </div>
          </Col>
          <Col sm={6}>
            <div className="text-center mb-3">
              <h2 className="text-success mb-1">
                {formatNumber(data.tokensDeployed)}
              </h2>
              <p className="text-muted mb-0">Registrados na Blockchain</p>
            </div>
          </Col>
        </Row>

        <div className="mb-4">
          <Row>
            <Col sm={12}>
              <div className="text-center mb-3">
                <h3 className="text-info mb-1">
                  {formatCurrency(data.totalTokenValue)}
                </h3>
                <p className="text-muted mb-0">Valor Total dos Recebíveis</p>
              </div>
            </Col>
          </Row>
        </div>

        <div className="mb-4">
          <div className="bg-light p-3 rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Valor Médio por Recebível</span>
              <span className="font-weight-bold text-primary">
                {formatCurrency(data.averageTokenValue)}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Taxa de Registro Blockchain</span>
              <span className="font-weight-bold text-success">
                {deploymentRate.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h6 className="mb-3">Distribuição por Status:</h6>
          {Object.entries(data.tokensByType).map(([type, count]) => {
            const getTypeInfo = (type: string) => {
              switch (type) {
                case 'settled':
                  return { label: 'Liquidados', color: 'success' }
                case 'active':
                  return { label: 'Ativos', color: 'primary' }
                case 'overdue':
                  return { label: 'Vencidos', color: 'danger' }
                default:
                  return { label: type, color: 'secondary' }
              }
            }

            const typeInfo = getTypeInfo(type)

            return (
              <div key={type} className="mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-capitalize">
                    <i
                      className={`feather ${
                        type === 'settled'
                          ? 'icon-check-circle'
                          : type === 'active'
                          ? 'icon-clock'
                          : type === 'overdue'
                          ? 'icon-alert-circle'
                          : 'icon-help-circle'
                      } mr-1`}
                    />
                    {typeInfo.label}
                  </span>
                  <span className={`badge badge-${typeInfo.color}`}>
                    {count}
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        <div className="border-top pt-3">
          <Row>
            <Col sm={12}>
              <div className="d-flex align-items-center justify-content-center text-muted">
                <i className="feather icon-trending-up mr-2" />
                <small>Crescimento constante na geração de recebíveis</small>
              </div>
            </Col>
          </Row>
        </div>

        <div className="mt-3 pt-3 border-top">
          <small className="text-muted">
            <i className="feather icon-shield mr-1" />
            Todos os recebíveis são registrados e gerenciados na blockchain
            Hyperledger Besu
          </small>
        </div>
      </Card.Body>
    </Card>
  )
}
